import React, { useEffect, useState } from 'react';
import { Newspaper, Clock, ChevronDown } from 'lucide-react';
import { API_URL } from '../../config';
import { useAuth } from '../../context/AuthContext';
import SiteLayout from '../../layouts/SiteLayout';
import Header from '../../components/Header/Header';
import CompanyLogo from '../../components/Common/CompanyLogo';
import ArticleImage, { ArticleImageData } from '../../components/Common/ArticleImage';

interface Publication {
    id: number;
    societe_symbole: string;
    exercice: number;
    period_type: string;
    titre: string;
    content_html: string;
    published_at: string | null;
    image?: ArticleImageData | null;
}

const PERIOD_LABELS: Record<string, string> = {
    ANNUAL: 'Résultats annuels', Q1: '1er trimestre', S1: '1er semestre',
    '9M': '9 mois', Q2: '2ème trimestre', Q3: '3ème trimestre', Q4: '4ème trimestre',
};

const PublicationsScreen: React.FC = () => {
    const { token } = useAuth();
    const [publications, setPublications] = useState<Publication[]>([]);
    const [loading, setLoading] = useState(true);
    const [expandedId, setExpandedId] = useState<number | null>(null);

    useEffect(() => {
        if (token) {
            fetchPublications();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    const fetchPublications = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${API_URL}/v1/publications?limit=50`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            if (response.ok) {
                const data = await response.json();
                setPublications(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching publications:', error);
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateStr: string | null) => {
        if (!dateStr) return '';
        return new Date(dateStr).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
    };

    return (
        <SiteLayout>
            <Header icon='auto_awesome' title='Analyses IA' />
            <div style={{ padding: '24px', maxWidth: '900px', margin: '0 auto' }}>
                {loading ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8' }}>
                        <Newspaper size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <div>Chargement des publications...</div>
                    </div>
                ) : publications.length === 0 ? (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#94a3b8', background: 'var(--bg-box)', borderRadius: '16px' }}>
                        <Newspaper size={48} style={{ marginBottom: '16px', opacity: 0.5 }} />
                        <div>Aucune publication pour le moment</div>
                    </div>
                ) : (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        {publications.map((p) => {
                            const isExpanded = expandedId === p.id;
                            return (
                                <div
                                    key={p.id}
                                    style={{ background: 'var(--bg-box)', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden' }}
                                >
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', padding: '16px', gap: '16px', cursor: 'pointer' }}
                                        onClick={() => setExpandedId(isExpanded ? null : p.id)}
                                    >
                                        <CompanyLogo symbol={p.societe_symbole} size={40} />
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
                                                <span style={{ fontWeight: 600, color: '#1e293b' }}>{p.titre}</span>
                                            </div>
                                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                                {p.societe_symbole} · {PERIOD_LABELS[p.period_type] || p.period_type} {p.exercice}
                                            </div>
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px', color: '#94a3b8' }}>
                                                <Clock size={14} />
                                                {formatDate(p.published_at)}
                                            </div>
                                            <ChevronDown
                                                size={20} color='#94a3b8'
                                                style={{ transform: isExpanded ? 'rotate(180deg)' : 'rotate(0)', transition: 'transform 0.2s' }}
                                            />
                                        </div>
                                    </div>
                                    {isExpanded && (
                                        <div style={{ padding: '16px 16px 16px 16px', borderTop: '1px solid #f1f5f9' }}>
                                            <ArticleImage image={p.image} />
                                            <div
                                                style={{ fontSize: '14px', color: '#475569', lineHeight: '1.6' }}
                                                dangerouslySetInnerHTML={{ __html: p.content_html }}
                                            />
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        </SiteLayout>
    );
};

export default PublicationsScreen;
